import type { GameStateManager } from '../game/GameState';
import type { GameData } from '../game/types';

export class ResourcePanel {
  private previousChemicals: number = 0;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData
  ) {
    const state = this.gameState.getState();
    this.previousChemicals = state.chemicals;
    this.update();
  }

  private createIndicator(chemicalsItem: Element, amount: number): void {
    // Get the position of the chemicals item relative to the container
    const itemRect = chemicalsItem.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();
    
    // Calculate position relative to container
    const relativeTop = itemRect.top - containerRect.top + itemRect.height / 2;
    const relativeLeft = itemRect.left - containerRect.left + itemRect.width / 2;

    const indicator = document.createElement('div');
    indicator.className = 'chemical-change-indicator';
    indicator.textContent = amount > 0 ? `+${amount}` : `${amount}`;
    indicator.style.color = amount > 0 ? '#4ade80' : '#f87171';
    indicator.style.top = `${relativeTop}px`;
    indicator.style.left = `${relativeLeft}px`;
    
    // Append to container (not contentDiv) so it survives innerHTML updates
    this.container.appendChild(indicator);

    // Remove after animation completes
    setTimeout(() => {
      if (indicator.parentNode) {
        indicator.remove();
      }
    }, 1000);
  }

  update(): void {
    const state = this.gameState.getState();
    const strings = this.gameData.uiStrings;
    const config = this.gameData.gameConfig;
    const drain = config.budget_drain_base + (state.researchers.length * config.budget_drain_per_researcher);

    // Check for chemical changes before updating
    const chemicalChange = state.chemicals - this.previousChemicals;
    const shouldShowIndicator = chemicalChange !== 0;
    this.previousChemicals = state.chemicals;

    // Only update the content, not the entire container (to preserve speed-controls)
    let contentDiv = this.container.querySelector('.resource-panel-content');
    if (!contentDiv) {
      contentDiv = document.createElement('div');
      contentDiv.className = 'resource-panel-content';
      this.container.insertBefore(contentDiv, this.container.querySelector('#speed-controls'));
    }

    contentDiv.innerHTML = `
      <div class="resource-item" data-tooltip-key="resource.budget">
        <div class="resource-label">${strings.labels?.budget || 'Budget'}</div>
        <div class="resource-value-with-drain">
          <span class="resource-value">${Math.floor(state.budget)}</span>
          <span class="resource-drain">-${drain}/tick</span>
        </div>
      </div>
      <div class="resource-item chemicals-item" style="position: relative;" data-tooltip-key="resource.chemicals">
        <div class="resource-label">${strings.labels?.chemicals || 'Chemicals'}</div>
        <div class="resource-value" style="position: relative;">${state.chemicals}/${state.maxChemicals}</div>
      </div>
      <div class="resource-item" data-tooltip-key="resource.computing">
        <div class="resource-label">${strings.labels?.computing || 'Computing'}</div>
        <div class="resource-value">
          <span class="computing-available">${state.computingAvailable}</span><span class="computing-separator">/</span><span class="computing-total">${state.computingAvailable + state.computingReserved}</span>
        </div>
      </div>
      <div class="resource-item" data-tooltip-key="resource.anchoring">
        <div class="resource-label">${strings.labels?.anchoring || 'Anchoring'}</div>
        <div class="resource-value">${state.anchoring}/${state.maxAnchoring}</div>
      </div>
    `;

    // Show indicator after HTML is updated
    if (shouldShowIndicator) {
      // Use requestAnimationFrame to ensure DOM is laid out before calculating positions
      requestAnimationFrame(() => {
        const chemicalsItem = contentDiv.querySelector('.chemicals-item');
        if (chemicalsItem) {
          this.createIndicator(chemicalsItem, chemicalChange);
        }
      });
    }
  }
}


